import { z } from 'zod';

/**
 * Specify your server-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars.
 */
const server = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']),
});

/**
 * Specify your client-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars. To expose them to the client, prefix them with `VITE_`.
 */
const client = z.object({
  VITE_KAPA_SERVICE_URL: z.string().min(1),
  VITE_POSTHOG_KEY:
    import.meta.env.MODE === 'production'
      ? z.string().min(1)
      : z.string().min(1).optional(),
  VITE_OTP_LENGTH: z.number().int().positive(),
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_ENVIRONMENT: z.string().optional(),
  // Optional: absent in local dev, where the backend leaves the login captcha
  // disabled and no token is expected.
  VITE_RECAPTCHA_SITE_KEY: z.string().optional(),
});

/**
 * Environment variables for Vite. In Vite, all env vars are accessed via import.meta.env
 * @type {Record<keyof z.infer<typeof server> | keyof z.infer<typeof client>, string | undefined>}
 */
const processEnv = {
  NODE_ENV: import.meta.env.MODE || 'development',
  KAPA_SERVICE_URL: import.meta.env.KAPA_SERVICE_URL,
  VITE_KAPA_SERVICE_URL: import.meta.env.VITE_KAPA_SERVICE_URL,
  VITE_POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
  VITE_OTP_LENGTH: parseInt(import.meta.env.VITE_OTP_LENGTH || '6'),
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  VITE_RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
};

// Don't touch the part below
// --------------------------

const merged = server.merge(client);

/** @typedef {z.input<typeof merged>} MergedInput */
/** @typedef {z.infer<typeof merged>} MergedOutput */
/** @typedef {z.SafeParseReturnType<MergedInput, MergedOutput>} MergedSafeParseReturn */

let env = /** @type {MergedOutput} */ (import.meta.env);

if (!import.meta.env.SKIP_ENV_VALIDATION) {
  const isServer = typeof window === 'undefined';

  const parsed = /** @type {MergedSafeParseReturn} */ (
    isServer
      ? merged.safeParse(processEnv) // on server we can validate all env vars
      : client.safeParse(processEnv) // on client we can only validate the ones that are exposed
  );

  if (parsed.success === false) {
    console.error(
      '❌ Invalid environment variables:',
      parsed.error.flatten().fieldErrors,
    );
    throw new Error('Invalid environment variables');
  }

  env = new Proxy(parsed.data, {
    get(target, prop) {
      if (typeof prop !== 'string') return undefined;
      // Throw a descriptive error if a server-side env var is accessed on the client
      // Otherwise it would just be returning `undefined` and be annoying to debug
      if (!isServer && !prop.startsWith('VITE_'))
        throw new Error(
          import.meta.env.MODE === 'production'
            ? '❌ Attempted to access a server-side environment variable on the client'
            : `❌ Attempted to access server-side environment variable '${prop}' on the client`,
        );
      return target[/** @type {keyof typeof target} */ (prop)];
    },
  });
}

export { env };
